import apiClient from './apiClient'
import { logger } from '../utils/logger'

export interface UserData {
  id: number
  admin_id: number
  name: string
  email: string
  type: 'attendant' | 'manager'
  status: 'active' | 'blocked'
  created_at: string
  updated_at: string
  sectors?: UserSector[]
}

export interface UserSector {
  id: number
  name: string
  slug: string
  is_active: boolean
}

export interface CreateUserRequest {
  name: string
  email: string
  password: string
  type: 'attendant' | 'manager'
  status?: 'active' | 'blocked'
  sector_ids?: number[]
}

export interface UpdateUserRequest {
  name?: string
  email?: string
  password?: string
  type?: 'attendant' | 'manager'
  status?: 'active' | 'blocked'
  sector_ids?: number[]
}

export const userService = {
  // Listar usuários com paginação
  async getUsers(page: number = 1, limit: number = 10): Promise<{
    users: UserData[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    // Adicionar timestamp para evitar cache
    const timestamp = new Date().getTime()
    const response = await apiClient.get(`/users?page=${page}&limit=${limit}&_t=${timestamp}`)
    return response.data.data
  },

  // Criar usuário
  async createUser(userData: CreateUserRequest): Promise<UserData> {
    const response = await apiClient.post('/users', userData)
    return response.data.data
  },

  // Atualizar usuário
  async updateUser(userId: number, userData: UpdateUserRequest): Promise<UserData> {
    logger.debug('🌐 Enviando requisição PUT para atualização de usuário', { userId, hasPassword: Boolean(userData.password) })
    try {
      const response = await apiClient.put(`/users/${userId}`, userData)
      logger.debug('✅ Atualização de usuário concluída com sucesso', { userId })
      return response.data.data
    } catch (error) {
      logger.error('❌ Erro na requisição:', error)
      throw error
    }
  },

  // Deletar usuário
  async deleteUser(userId: number): Promise<void> {
    await apiClient.delete(`/users/${userId}`)
  },

  // Obter usuário por ID
  async getUserById(userId: number): Promise<UserData> {
    const response = await apiClient.get(`/users/${userId}`)
    return response.data.data
  },

  async getSectors(): Promise<UserSector[]> {
    const response = await apiClient.get('/users/sectors')
    return response.data.data || []
  },

  async createSector(name: string): Promise<UserSector> {
    const response = await apiClient.post('/users/sectors', { name })
    return response.data.data
  },

  async updateSector(sectorId: number, name: string): Promise<UserSector> {
    const response = await apiClient.put(`/users/sectors/${sectorId}`, { name })
    return response.data.data
  },

  async deleteSector(sectorId: number): Promise<void> {
    await apiClient.delete(`/users/sectors/${sectorId}`)
  }
}
